import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Shield, FileText, Mail, AlertTriangle, Scale, Send, RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function DMCAPage() {
  return (
    <div className="min-h-screen py-12 px-4" data-testid="dmca-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/20 text-primary">Legal</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase mb-4">
            DMCA Copyright Policy
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            DMV Soundstage respects the intellectual property rights of artists, creators, 
            and copyright holders and complies with the Digital Millennium Copyright Act (DMCA).
          </p>
        </div>

        {/* Important Notice */}
        <Card className="glass-card border-amber-500/30 p-6 mb-8 bg-amber-500/5">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" />
            <div>
              <h2 className="font-heading text-lg font-bold uppercase mb-2 text-amber-400">Important</h2>
              <p className="text-muted-foreground">
                Artists and users may only upload or link content they own or have authorization to use.
                Violation of copyright policies may result in content removal and account termination.
              </p>
            </div>
          </div>
        </Card>

        <div className="space-y-8">
          {/* Reporting Copyright Infringement */}
          <Card className="glass-card border-border p-6" data-testid="dmca-reporting">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-red-500" />
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-xl font-bold uppercase mb-4">
                  Reporting Copyright Infringement
                </h2>
                <p className="text-muted-foreground mb-4">
                  If you believe that content on DMV Soundstage infringes your copyright, 
                  please submit a written notice including:
                </p>
                <ol className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center text-secondary text-sm font-bold flex-shrink-0">1</span>
                    <span>Your full legal name and contact information</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center text-secondary text-sm font-bold flex-shrink-0">2</span>
                    <span>Identification of the copyrighted work</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center text-secondary text-sm font-bold flex-shrink-0">3</span>
                    <span>The URL or description of the allegedly infringing content</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center text-secondary text-sm font-bold flex-shrink-0">4</span>
                    <span>A statement that you believe the use is unauthorized</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center text-secondary text-sm font-bold flex-shrink-0">5</span>
                    <span>A statement made under penalty of perjury that the information is accurate</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center text-secondary text-sm font-bold flex-shrink-0">6</span>
                    <span>Your physical or electronic signature</span>
                  </li>
                </ol>
                
                <div className="mt-6 p-4 rounded-lg bg-muted/30 border border-border">
                  <p className="text-sm text-muted-foreground mb-2">Send DMCA notices to:</p>
                  <a 
                    href="mailto:support@dmvsoundstage.com?subject=DMCA%20Notice" 
                    className="flex items-center gap-2 text-secondary hover:underline"
                  >
                    <Mail className="w-4 h-4" />
                    support@dmvsoundstage.com
                  </a>
                </div>
              </div>
            </div>
          </Card>

          {/* Takedown & Repeat Infringers */}
          <Card className="glass-card border-border p-6" data-testid="dmca-takedown">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold uppercase mb-4">
                  Takedown & Repeat Infringers
                </h2>
                <p className="text-muted-foreground mb-4">
                  Upon receiving a valid DMCA notice, DMV Soundstage will:
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-secondary">•</span>
                    Remove or disable access to the infringing content
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-secondary">•</span>
                    Notify the uploader of the takedown
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-secondary">•</span>
                    Track repeat infringement violations
                  </li>
                </ul>
                <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                  <p className="text-sm text-red-400">
                    <strong>Warning:</strong> Users who repeatedly infringe copyrights may have their accounts permanently terminated.
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Counter-Notification */}
          <Card className="glass-card border-border p-6" data-testid="dmca-counter">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <RotateCcw className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold uppercase mb-4">
                  Counter-Notification
                </h2>
                <p className="text-muted-foreground mb-4">
                  If you believe content was removed in error, you may submit a counter-notification including:
                </p>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-secondary">•</span>
                    Your contact information
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-secondary">•</span>
                    Identification of the removed content
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-secondary">•</span>
                    A statement under penalty of perjury that removal was a mistake
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-secondary">•</span>
                    Consent to jurisdiction of applicable courts
                  </li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Disclaimer */}
          <Card className="glass-card border-border p-6" data-testid="dmca-disclaimer">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-gray-500/20 flex items-center justify-center flex-shrink-0">
                <Scale className="w-6 h-6 text-gray-400" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold uppercase mb-4">
                  Disclaimer
                </h2>
                <p className="text-muted-foreground">
                  DMV Soundstage acts as a service provider and does not claim ownership over 
                  user-uploaded content. Responsibility for uploaded content remains with the uploader.
                </p>
                <p className="text-muted-foreground mt-3">
                  DMV Soundstage reserves the right to update this policy at any time.
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Footer Links */}
        <div className="mt-12 text-center text-sm text-muted-foreground">
          <p>Last updated: January 2025</p>
          <div className="mt-4 flex justify-center gap-4">
            <Link to="/community-guidelines" className="text-secondary hover:underline">Community Guidelines</Link>
            <span className="text-muted-foreground/40">|</span>
            <Link to="/terms" className="text-secondary hover:underline">Terms of Service</Link>
            <span className="text-muted-foreground/40">|</span>
            <Link to="/contact" className="text-secondary hover:underline">Contact Us</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
